/**
 * A tiny Web Audio dub-techno engine: muffled kick, offbeat hats, a filtered
 * minor-9 chord stab feeding a dotted-eighth dub delay, vinyl hiss, and
 * impact hits on every cut.
 */

export interface DubEngine {
  ctx: AudioContext;
  master: GainNode;
  delaySend: GainNode;
  noise: AudioBuffer;
}

const CHORD = [146.83, 174.61, 220, 261.63, 329.63]; // D minor 9

export function createDubEngine(ctx: AudioContext, stepDuration: number): DubEngine {
  const master = ctx.createGain();
  master.gain.value = 0;
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -16;
  comp.ratio.value = 4;
  comp.attack.value = 0.005;
  comp.release.value = 0.2;
  master.connect(comp).connect(ctx.destination);

  // Dub delay with filtered feedback.
  const delay = ctx.createDelay(2);
  delay.delayTime.value = stepDuration * 3;
  const fbLow = ctx.createBiquadFilter();
  fbLow.type = 'lowpass';
  fbLow.frequency.value = 1700;
  const fbHigh = ctx.createBiquadFilter();
  fbHigh.type = 'highpass';
  fbHigh.frequency.value = 280;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.58;
  delay.connect(fbLow).connect(fbHigh).connect(feedback).connect(delay);
  const wet = ctx.createGain();
  wet.gain.value = 0.7;
  fbHigh.connect(wet).connect(master);
  const delaySend = ctx.createGain();
  delaySend.connect(delay);

  // Slow filter movement on the delay, the classic dub "breathing".
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.12;
  const lfoDepth = ctx.createGain();
  lfoDepth.gain.value = 700;
  lfo.connect(lfoDepth).connect(fbLow.frequency);
  lfo.start();

  const noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = noise.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

  // Vinyl hiss bed.
  const hiss = ctx.createBufferSource();
  hiss.buffer = noise;
  hiss.loop = true;
  const hissFilter = ctx.createBiquadFilter();
  hissFilter.type = 'bandpass';
  hissFilter.frequency.value = 3200;
  hissFilter.Q.value = 0.6;
  const hissGain = ctx.createGain();
  hissGain.gain.value = 0.014;
  hiss.connect(hissFilter).connect(hissGain).connect(master);
  hiss.start();

  return { ctx, master, delaySend, noise };
}

export function kick(e: DubEngine, at: number, vel = 1) {
  const { ctx } = e;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.frequency.setValueAtTime(150, at);
  osc.frequency.exponentialRampToValueAtTime(44, at + 0.13);
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(0.95 * vel, at + 0.004);
  g.gain.exponentialRampToValueAtTime(0.001, at + 0.5);
  osc.connect(g).connect(e.master);
  osc.start(at);
  osc.stop(at + 0.55);
}

export function hat(e: DubEngine, at: number, vel = 1) {
  const { ctx } = e;
  const src = ctx.createBufferSource();
  src.buffer = e.noise;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 7800;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.22 * vel, at);
  g.gain.exponentialRampToValueAtTime(0.001, at + 0.06);
  src.connect(hp).connect(g).connect(e.master);
  src.start(at, Math.random() * 0.5);
  src.stop(at + 0.08);
}

export function stab(e: DubEngine, at: number, vel = 1, send = 0.9) {
  const { ctx } = e;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 7;
  filter.frequency.setValueAtTime(2600 * vel, at);
  filter.frequency.exponentialRampToValueAtTime(320, at + 0.2);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(0.06 * vel, at + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.26);
  CHORD.forEach((f, i) => {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = f;
    osc.detune.value = (i % 2 ? 1 : -1) * 7;
    osc.connect(filter);
    osc.start(at);
    osc.stop(at + 0.3);
  });
  filter.connect(g);
  g.connect(e.master);
  const s = ctx.createGain();
  s.gain.value = send;
  g.connect(s).connect(e.delaySend);
}

export function impact(e: DubEngine, at: number, vel = 1) {
  const { ctx } = e;
  const src = ctx.createBufferSource();
  src.buffer = e.noise;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(1400, at);
  bp.frequency.exponentialRampToValueAtTime(300, at + 0.25);
  bp.Q.value = 1.2;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.32 * vel, at);
  g.gain.exponentialRampToValueAtTime(0.001, at + 0.3);
  src.connect(bp).connect(g).connect(e.master);
  const s = ctx.createGain();
  s.gain.value = 0.5;
  g.connect(s).connect(e.delaySend);
  src.start(at, Math.random() * 0.5);
  src.stop(at + 0.32);
}

export function rim(e: DubEngine, at: number) {
  const { ctx } = e;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = 1750;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.18, at);
  g.gain.exponentialRampToValueAtTime(0.001, at + 0.035);
  osc.connect(g);
  g.connect(e.master);
  g.connect(e.delaySend);
  osc.start(at);
  osc.stop(at + 0.05);
}

/** Filtered noise sweep that builds into the drop. */
export function riser(e: DubEngine, at: number, duration: number) {
  const { ctx } = e;
  const src = ctx.createBufferSource();
  src.buffer = e.noise;
  src.loop = true;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 2;
  bp.frequency.setValueAtTime(400, at);
  bp.frequency.exponentialRampToValueAtTime(6500, at + duration);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(0.16, at + duration - 0.02);
  g.gain.setValueAtTime(0.0001, at + duration);
  src.connect(bp).connect(g).connect(e.master);
  src.start(at);
  src.stop(at + duration + 0.02);
}
